import { useState, useEffect } from "react";
import axios from "axios";
import { Upload, Calendar, Users, Clock, AlertTriangle, ChevronDown, ChevronUp, Trash2, FileSpreadsheet, Building2, Download, ArrowLeft, Search, Filter, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function ColHeader({ label, tooltip }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help border-b border-dotted border-muted-foreground/50">{label}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px] text-xs">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function AttendancePage() {
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadMonth, setUploadMonth] = useState(String(new Date().getMonth() + 1));
  const [uploadYear, setUploadYear] = useState(String(new Date().getFullYear()));
  const [machineFile, setMachineFile] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [expandedDept, setExpandedDept] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/attendance/months`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data);
    } catch (err) {
      console.error("Failed to fetch records:", err);
    }
  };

  const fetchRecord = async (recordId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/attendance/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedRecord(res.data);
    } catch (err) {
      toast.error("Failed to fetch attendance data");
    }
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!machineFile && !manualFile) {
      setUploadError("Please select at least one file");
      return;
    }
    setUploading(true);
    setUploadError("");
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("month", uploadMonth);
      formData.append("year", uploadYear);
      if (machineFile) formData.append("machine_file", machineFile);
      if (manualFile) formData.append("manual_file", manualFile);

      const res = await axios.post(`${API}/api/attendance/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success(`${res.data.message} (${res.data.matched} matched)`);
      setShowUploadDialog(false);
      setMachineFile(null);
      setManualFile(null);
      setUploadError("");
      fetchRecords();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed. Please try again.";
      setUploadError(msg);
    }
    setUploading(false);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm("Delete this attendance record?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/attendance/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Record deleted");
      if (selectedRecord?.id === recordId) setSelectedRecord(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleDownloadExcel = async () => {
    if (!selectedRecord) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/attendance/${selectedRecord.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 60000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Attendance_${MONTHS[selectedRecord.month - 1]}_${selectedRecord.year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded successfully");
    } catch (err) {
      toast.error("Failed to download Excel. Please try again.");
    }
    setDownloading(false);
  };

  // Get filtered employees
  const employees = selectedRecord?.employees || [];
  const departments = [...new Set(employees.map(e => e.department || "Unknown"))].sort();
  const filteredEmployees = employees.filter(e => {
    const matchSearch = !searchQuery || e.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchDept = deptFilter === "all" || (e.department || "Unknown") === deptFilter;
    const matchSource = sourceFilter === "all" || e.source === sourceFilter;
    return matchSearch && matchDept && matchSource;
  });

  // Overall stats
  const totalDiscrepancies = employees.filter(e => e.discrepancies?.length > 0).length;
  const matchedCount = employees.filter(e => e.source === "both").length;
  const machineOnlyCount = employees.filter(e => e.source === "machine").length;
  const manualOnlyCount = employees.filter(e => e.source === "manual").length;

  const deptSummary = selectedRecord?.dept_summary || {};

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Attendance</h1>
          <p className="text-muted-foreground text-sm mt-1">Upload biometric (machine) & manual salary reports to compare attendance</p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)} data-testid="upload-attendance-btn">
          <Upload className="w-4 h-4 mr-2" />
          Upload Reports
        </Button>
      </div>

      {/* Month Records List */}
      {!selectedRecord && !loading && (
        <div className="grid gap-3">
          {records.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No attendance records yet. Upload your first report.</p>
              </CardContent>
            </Card>
          ) : (
            records.map((r) => (
              <Card
                key={r.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => fetchRecord(r.id)}
                data-testid={`attendance-record-${r.id}`}
              >
                <CardContent className="py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-semibold">{MONTHS[r.month - 1]} {r.year}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.total_employees} employees | Uploaded by {r.uploaded_by}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.machine_uploaded && <Badge variant="outline" className="text-xs">Biometric</Badge>}
                    {r.manual_uploaded && <Badge variant="outline" className="text-xs">Manual</Badge>}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                      data-testid={`delete-record-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mr-3" />
          <span className="text-muted-foreground">Loading attendance data...</span>
        </div>
      )}

      {/* Selected Record Detail View */}
      {selectedRecord && !loading && (
        <>
          {/* Top bar with Back, Title, Download */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => { setSelectedRecord(null); setDeptFilter("all"); setSourceFilter("all"); setSearchQuery(""); }} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <h2 className="text-lg font-semibold">
                {MONTHS[selectedRecord.month - 1]} {selectedRecord.year}
              </h2>
              <Badge variant="secondary">{selectedRecord.total_employees} employees</Badge>
            </div>
            <Button onClick={handleDownloadExcel} disabled={downloading} data-testid="download-excel-btn" className="gap-2">
              {downloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download Excel
                </>
              )}
            </Button>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground font-medium">Matched (Both Reports)</p>
                <p className="text-xl font-bold text-green-700">{matchedCount}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground font-medium">Biometric Only</p>
                <p className="text-xl font-bold text-blue-700">{machineOnlyCount}</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200 bg-gray-50/50">
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground font-medium">Manual Only</p>
                <p className="text-xl font-bold text-gray-700">{manualOnlyCount}</p>
              </CardContent>
            </Card>
            <Card className={`${totalDiscrepancies > 0 ? "border-amber-200 bg-amber-50/50" : "border-green-200 bg-green-50/50"}`}>
              <CardContent className="py-3 px-4">
                <p className="text-xs text-muted-foreground font-medium">Mismatches Found</p>
                <p className={`text-xl font-bold ${totalDiscrepancies > 0 ? "text-amber-700" : "text-green-700"}`}>{totalDiscrepancies}</p>
              </CardContent>
            </Card>
          </div>

          {/* Department Summary Cards */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Department Summary (click to filter)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {Object.entries(deptSummary).map(([dept, s]) => (
                <Card
                  key={dept}
                  className={`cursor-pointer transition-all hover:shadow-sm ${expandedDept === dept ? "ring-2 ring-primary shadow-md" : ""}`}
                  onClick={() => {
                    if (expandedDept === dept) {
                      setDeptFilter("all");
                      setExpandedDept(null);
                    } else {
                      setDeptFilter(dept);
                      setExpandedDept(dept);
                    }
                  }}
                >
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                      <p className="text-xs font-semibold truncate">{dept}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-y-1 text-xs">
                      <div><span className="text-muted-foreground">Employees:</span></div>
                      <div className="font-medium text-right">{s.total_employees}</div>
                      <div><span className="text-muted-foreground">Total Days:</span></div>
                      <div className="font-medium text-right">{s.total_present_days}</div>
                      <div><span className="text-muted-foreground">Overtime:</span></div>
                      <div className="font-medium text-orange-600 text-right">{s.total_ot_hours}h</div>
                      <div><span className="text-muted-foreground">Salary:</span></div>
                      <div className="font-medium text-right">{(s.total_salary / 1000).toFixed(0)}K</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative sm:max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search employee name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="attendance-search"
              />
            </div>
            <Select value={deptFilter} onValueChange={(v) => { setDeptFilter(v); setExpandedDept(v === "all" ? null : v); }}>
              <SelectTrigger className="sm:max-w-[200px]" data-testid="dept-filter">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="sm:max-w-[180px]" data-testid="source-filter">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="both">Matched (Both)</SelectItem>
                <SelectItem value="machine">Biometric Only</SelectItem>
                <SelectItem value="manual">Manual Only</SelectItem>
              </SelectContent>
            </Select>
            <Badge variant="secondary" className="self-center whitespace-nowrap">{filteredEmployees.length} of {employees.length}</Badge>
          </div>

          {/* Legend / Help */}
          <div className="bg-muted/40 rounded-lg px-4 py-3 text-xs text-muted-foreground space-y-1 border">
            <p className="font-semibold text-foreground text-sm mb-1">How to read this table</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
              <p><span className="font-medium text-foreground">Biometric Present / Absent</span> = Days marked by biometric machine</p>
              <p><span className="font-medium text-foreground">Biometric OT</span> = Overtime hours from biometric machine</p>
              <p><span className="font-medium text-foreground">Manual Days</span> = Working days from manual salary sheet</p>
              <p><span className="font-medium text-foreground">Manual Hours</span> = Total hours from manual salary sheet</p>
              <p><span className="font-medium text-foreground">CL</span> = Casual Leave taken (from manual)</p>
              <p><span className="font-medium text-foreground">Match Status</span> = Whether employee found in both reports</p>
              <p className="sm:col-span-2"><AlertTriangle className="w-3 h-3 text-amber-500 inline mr-1" /><span className="font-medium text-foreground">Warning icon</span> = Present-day count mismatch between biometric and manual report</p>
            </div>
          </div>

          {/* Employee Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs bg-muted/30">
                      <TableHead className="min-w-[160px] font-semibold">Employee Name</TableHead>
                      <TableHead className="hidden md:table-cell">Department</TableHead>
                      <TableHead className="hidden lg:table-cell">Post</TableHead>
                      <TableHead className="text-center">
                        <ColHeader label="Shift" tooltip="Working hours per shift: 8h or 12h" />
                      </TableHead>
                      <TableHead className="text-center bg-blue-50/50">
                        <ColHeader label="Bio. Present" tooltip="Days marked Present in biometric machine report" />
                      </TableHead>
                      <TableHead className="text-center bg-blue-50/50">
                        <ColHeader label="Bio. Absent" tooltip="Days marked Absent in biometric machine report" />
                      </TableHead>
                      <TableHead className="text-center bg-blue-50/50 hidden sm:table-cell">
                        <ColHeader label="Bio. Hours" tooltip="Total work hours from biometric machine" />
                      </TableHead>
                      <TableHead className="text-center bg-blue-50/50 hidden sm:table-cell">
                        <ColHeader label="Bio. OT" tooltip="Overtime hours from biometric machine" />
                      </TableHead>
                      <TableHead className="text-center bg-green-50/50">
                        <ColHeader label="Man. Days" tooltip="Total working days from manual salary sheet" />
                      </TableHead>
                      <TableHead className="text-center bg-green-50/50 hidden sm:table-cell">
                        <ColHeader label="Man. Hours" tooltip="Total hours from manual salary sheet" />
                      </TableHead>
                      <TableHead className="text-center bg-green-50/50 hidden md:table-cell">
                        <ColHeader label="CL" tooltip="Casual Leave (from manual report)" />
                      </TableHead>
                      <TableHead className="text-center bg-green-50/50 hidden md:table-cell">
                        <ColHeader label="OT (calc)" tooltip="Calculated overtime: hours exceeding shift (8h/12h) from manual daily data" />
                      </TableHead>
                      <TableHead className="text-center hidden lg:table-cell">
                        <ColHeader label="Salary" tooltip="Salary amount from manual report" />
                      </TableHead>
                      <TableHead className="text-center">
                        <ColHeader label="Status" tooltip="Whether employee was found in both reports, only biometric, or only manual" />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEmployees.map((emp, idx) => (
                      <TableRow key={idx} className={emp.discrepancies?.length ? "bg-amber-50/60 hover:bg-amber-50" : "hover:bg-muted/20"} data-testid={`employee-row-${idx}`}>
                        <TableCell className="font-medium text-sm py-2.5">
                          <div className="flex items-center gap-1.5">
                            {emp.name}
                            {emp.discrepancies?.length > 0 && (
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                  </TooltipTrigger>
                                  <TooltipContent side="right" className="max-w-[250px]">
                                    {emp.discrepancies.map((d, i) => <p key={i} className="text-xs">{d}</p>)}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          <span className="md:hidden block text-xs text-muted-foreground mt-0.5">{emp.department}</span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{emp.department}</TableCell>
                        <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{emp.post}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={emp.shift_type === "12" ? "default" : "secondary"} className="text-xs">
                            {emp.shift_type}h
                          </Badge>
                        </TableCell>
                        {/* Biometric columns */}
                        <TableCell className="text-center text-sm bg-blue-50/20">{emp.machine_present || "-"}</TableCell>
                        <TableCell className="text-center text-sm bg-blue-50/20">{emp.machine_absent || "-"}</TableCell>
                        <TableCell className="text-center text-sm bg-blue-50/20 hidden sm:table-cell">{emp.machine_total_hours || "-"}</TableCell>
                        <TableCell className="text-center text-sm bg-blue-50/20 font-medium text-orange-600 hidden sm:table-cell">
                          {emp.machine_ot_hours !== "00:00" ? emp.machine_ot_hours : "-"}
                        </TableCell>
                        {/* Manual columns */}
                        <TableCell className="text-center text-sm bg-green-50/20">{emp.manual_days_worked || "-"}</TableCell>
                        <TableCell className="text-center text-sm bg-green-50/20 hidden sm:table-cell">{emp.manual_total_hours || "-"}</TableCell>
                        <TableCell className="text-center text-sm bg-green-50/20 hidden md:table-cell">{emp.manual_cl || "-"}</TableCell>
                        <TableCell className="text-center text-sm bg-green-50/20 font-medium text-orange-600 hidden md:table-cell">
                          {emp.overtime_hours > 0 ? `${emp.overtime_hours}h` : "-"}
                        </TableCell>
                        <TableCell className="text-center text-sm hidden lg:table-cell">
                          {emp.manual_salary ? `${(emp.manual_salary / 1000).toFixed(1)}K` : "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {emp.source === "both" ? (
                            <Badge variant="outline" className="text-xs text-green-700 border-green-300 bg-green-50">Matched</Badge>
                          ) : emp.source === "machine" ? (
                            <Badge variant="outline" className="text-xs text-blue-700 border-blue-300 bg-blue-50">Bio Only</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-gray-600 border-gray-300 bg-gray-50">Manual Only</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredEmployees.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={14} className="text-center py-8 text-muted-foreground">
                          No employees found matching your filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { if (!uploading) { setShowUploadDialog(open); setUploadError(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Attendance Reports</DialogTitle>
            <DialogDescription>Upload biometric machine (.xls) and/or manual salary (.xlsx) reports</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Month</Label>
                <Select value={uploadMonth} onValueChange={setUploadMonth}>
                  <SelectTrigger data-testid="upload-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Input
                  type="number"
                  value={uploadYear}
                  onChange={(e) => setUploadYear(e.target.value)}
                  data-testid="upload-year"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Biometric Machine Report (.xls)</Label>
              <p className="text-xs text-muted-foreground mb-1">The auto-generated report from the biometric attendance machine</p>
              <Input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setMachineFile(e.target.files[0])}
                data-testid="machine-file-input"
              />
            </div>
            <div>
              <Label className="text-sm">Manual Salary Report (.xlsx)</Label>
              <p className="text-xs text-muted-foreground mb-1">The manually prepared monthly salary/attendance sheet</p>
              <Input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setManualFile(e.target.files[0])}
                data-testid="manual-file-input"
              />
            </div>
            {uploadError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-testid="upload-error">
                {uploadError}
              </div>
            )}
            {uploading && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                Processing files... This may take a moment.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { if (!uploading) setShowUploadDialog(false); }} disabled={uploading}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || (!machineFile && !manualFile)} data-testid="confirm-upload-btn">
              {uploading ? "Processing..." : "Upload & Process"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
